<script setup>
/**
 * The Lessons feed's bottom (Claude outputs/community-feed-invite-2026-10-30/
 * DESIGN.md §3): a hairline ornament, "Looking for something?", and the same
 * LessonSearch, its panel opening upward. No box, no border, no second line.
 *
 * Typing here answers here: the result line, up to three Mystics, the
 * lessons, More lessons, all under this field; the stream above stays. Under
 * the results, always (results, none, or a failed search), once the first
 * answer is in: "✦ Ask {Mystic} about “{their words}”". A tap emits
 * `ask(words, sub, 'foot')`: the room, then the reading, the full words
 * waiting in its box, focused, not sent.
 *
 * LessonsTab hides it while the head's search answers.
 */
import LessonCard from '@/components/ui/share/LessonCard.vue'
import LoadState from '@/components/ui/LoadState.vue'
import LessonSearch from './LessonSearch.vue'
import MysticResult from './MysticResult.vue'
import { logSearch } from '@/api-service'
import { track } from '@/utils/analytics'
import { openMystic } from '@/utils/lesson'
import { FOOT_LINE, footWords, noMatchTitle, moreLabel } from '@/utils/community-lessons'
import { useLessonSearch } from '@/composables/useLessonSearch'

defineProps({
  placeholders: { type: Array, default: () => [] },
  asking: { type: Array, default: () => [] },
})
const emit = defineEmits(['ask', 'open-lesson'])

const { query, q, searching, results, runSearch, noMatch, line, asker, onSubmit, onPick, clear } = useLessonSearch({ via: 'foot' })

function openLesson(item) {
  if (!item || !item.short_uuid) return
  if (q.value) logSearch({ q: q.value, kind: 'tap' })
  emit('open-lesson', item.short_uuid)
}
function openResultMystic(m) {
  if (!m || !m.sub_domain) return
  logSearch({ q: q.value, kind: 'tap', sub_domain: m.sub_domain })
  openMystic(m.sub_domain, 'search')
}
function ask() {
  track('lesson_ask', { via: 'foot' })
  emit('ask', q.value, asker.value.sub, 'foot')
}
</script>

<template>
  <section class="ft" aria-labelledby="lessons-foot-h" data-testid="lessons-foot">
    <p class="ft-mark" aria-hidden="true">✦</p>
    <h2 id="lessons-foot-h" class="ft-h">{{ FOOT_LINE }}</h2>
    <LessonSearch v-model="query" :placeholders="placeholders" :asking="asking" panel-id="lesson-search-panel-foot" drop="up"
                  @submit="onSubmit" @pick="onPick" />

    <div v-if="searching" class="ft-res" data-testid="lessons-foot-results">
      <template v-if="!noMatch">
        <div v-if="results.done && !results.failed" class="rs-line" aria-live="polite">
          <span><b>{{ line.count }} {{ line.noun }}</b> {{ line.about }}{{ line.mystics }}</span>
          <button type="button" class="rs-clear" data-testid="lessons-foot-clear" @click="clear">Clear</button>
        </div>
        <div v-if="results.mystics.length" class="mys" aria-label="Mystics">
          <MysticResult v-for="m in results.mystics" :key="m.sub_domain" :mystic="m" @open="openResultMystic" />
        </div>
        <p v-if="results.lessons.length" class="sec">Lessons</p>
        <div v-if="results.lessons.length" class="grid">
          <LessonCard v-for="(item, i) in results.lessons" :key="item.short_uuid || i" class="lx-card" :lesson="item" interactive @open="openLesson(item)" />
        </div>
        <LoadState v-if="!results.lessons.length" :loading="results.loading || !results.done" :failed="results.failed"
                   message="Couldn't search just now." loading-label="Searching" @retry="runSearch(true)" />
        <button v-if="results.hasMore && results.lessons.length" type="button" class="more-btn" :disabled="results.loading"
                data-testid="lessons-foot-more" @click="runSearch(false)">{{ results.loading ? 'Drawing…' : moreLabel('') }}</button>
      </template>
      <template v-else>
        <div v-if="results.mystics.length" class="mys" aria-label="Mystics">
          <MysticResult v-for="m in results.mystics" :key="m.sub_domain" :mystic="m" @open="openResultMystic" />
        </div>
        <p class="nomatch" aria-live="polite">{{ noMatchTitle({ query: results.q }) }}</p>
      </template>

      <div v-if="results.done" class="ft-ask">
        <button type="button" class="lx-btn" data-testid="lessons-foot-ask" @click="ask"><span>✦ Ask {{ asker.name }} about <span class="w">“{{ footWords(q) }}”</span></span></button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.ft {
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif; --display: 'Letter Display', 'Cinzel', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  box-sizing: border-box; max-width: 944px; margin: 30px auto 0; padding: 0 16px; text-align: center; font-family: var(--ui);
}
.ft *, .ft *::before, .ft *::after { box-sizing: border-box; }
/* Two gold hairlines meeting a small ✦. */
.ft-mark { display: flex; align-items: center; gap: 14px; justify-content: center; max-width: 640px; margin: 0 auto 18px; color: rgba(231, 201, 131, .6); font-size: 13px; line-height: 1; }
.ft-mark::before, .ft-mark::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(231, 201, 131, .35)); }
.ft-mark::after { background: linear-gradient(90deg, rgba(231, 201, 131, .35), transparent); }
.ft-h { margin: 0; font: 500 25px/1.25 var(--serif); color: #f6edff; }
.ft :deep(.sx) { margin-top: 16px; }
.ft-res { text-align: left; }

.sec { margin: 0 0 14px; font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: #cdb98a; }
.grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
.grid > .lx-card { width: 100%; max-width: none; }
.rs-line { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin: 22px 0 14px; color: #c9b8e6; font-size: 15px; }
.rs-line b { font: 600 22px var(--serif); color: #fbf3ff; }
.rs-clear { flex: none; border: 0; background: none; color: #c9b8e6; font: 500 14px var(--ui); text-decoration: underline; text-underline-offset: 3px; cursor: pointer;
  min-height: 44px; min-width: 44px; padding: 0 0 0 8px; margin: -10px 0; }
.mys { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 22px 0 26px; }
.rs-line + .mys { margin-top: 0; }
.nomatch { margin: 30px 0 0; text-align: center; font: 600 23px/1.2 var(--serif); color: #fff6e4; overflow-wrap: anywhere; }
.more-btn { display: block; margin: 26px auto 0; height: 46px; padding: 0 24px; border-radius: 23px; border: 1px solid rgba(231, 201, 131, .45); background: none;
  color: #ffe9b8; font: 600 14px var(--ui); cursor: pointer; }
.more-btn:hover:not(:disabled) { border-color: rgba(240, 217, 163, .8); }

/* "✦ Ask {Mystic} about “…”": the old box's pill, one sentence that may wrap. */
.ft-ask { display: flex; justify-content: center; margin: 26px 0 8px; }
.lx-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 52px; padding: 14px 28px; border-radius: 999px; white-space: normal;
  border: 1px solid rgba(240, 217, 163, .55); background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; text-align: center;
  font: 700 15px var(--ui); box-shadow: 0 12px 30px -8px rgba(78, 39, 118, .8); cursor: pointer; max-width: 100%;
}
.lx-btn:hover { filter: brightness(1.08); }
.lx-btn .w { font-weight: 600; color: #fff3d6; overflow-wrap: anywhere; }
.more-btn:focus-visible, .lx-btn:focus-visible, .rs-clear:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }

@media (min-width: 640px) {
  .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: stretch; }
}
@media (min-width: 768px) {
  .mys { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
  .nomatch { font-size: 26px; }
}
@media (min-width: 1024px) {
  .grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }
  .ft { padding: 0; }
}
</style>
